import { lazy, Suspense, useEffect, useState } from "react";

import { Link, Navigate, Route, Routes, useLocation } from "react-router";

import { Alert, Flex, Spinner, Text } from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { AssignmentTypePanel } from "./components/AssignmentType/AssignmentTypePanel";
import CourseCodePattern from "./components/CourseCodePattern";
import Demographics from "./components/Demographics";
import { AttainmentLevelsConfig } from "./components/LearningOutcomes/AttainmentLevels/AttainmentLevelsConfig";
import { InstitutionalLearningOutcome } from "./components/LearningOutcomes/ILO/InstitutionalLearningOutcome";
import { ProgramLearningOutcomes } from "./components/LearningOutcomes/PLO/ProgramLearningOutcomes";
import LearningTaxonomy from "./components/LearningTaxonomy";
import MasteryLevelsAndRatingScales from "./components/MasteryLevelsAndRatingScales/MasteryLevelsAndRatingScales";
import Users from "./components/Users/Users";
import { UserService } from "./services/UserService";
import {
  FeatureContext,
  LoadingContext,
  MasteryLevelContext,
  TermsContext,
  UserDetailContext,
} from "./utils/Context";
import {
  AssessmentPlanningDashboard,
  CurriculumMap,
  Dashboard,
  Outcomes,
  Plan,
  Setup,
} from "./views";

import { Trans } from "react-i18next";
import { SaveAlert, StatusAlert } from "./components/Alerts";
import Results from "./components/Assessment/APDashboard/Results/Results";
import ResultsReport from "./components/Assessment/APDashboard/Results/ResultsReport";
import CreatePlanFromTemplate from "./components/Assessment/Plan/CreatePlanFromTemplate";
import ManagePlan from "./components/Assessment/Plan/ManagePlan";
import Rater from "./components/Assessment/Rater/Rater";
import CourseActions from "./components/CourseReview/Actions/CourseActions";
import CourseResults from "./components/CourseReview/CourseResults";
import { CourseReview } from "./components/CourseReview/CourseReview";
import CourseReviewHome from "./components/CourseReview/CourseReviewHome";
import CourseReviewSummary from "./components/CourseReview/CourseReviewSummary";
import CourseReflections from "./components/CourseReview/Reflections/CourseReflections";
import {
  ERROR,
  useNotification,
  WARNING,
} from "./components/CustomHooks/NotificationProvider";
import { CyclesAndGoals } from "./components/CyclesAndGoals/CyclesAndGoals";
import Error from "./components/Error";
import Nav from "./components/Nav/Nav";
import TopNav from "./components/Nav/TopNav";
import { isAllowedRoute } from "./components/Nav/utils";
import { OrganizationalStructure } from "./components/OrganizationalStructure/OrganizationalStructure";
import Templates from "./components/PlanTemplates/PlanTemplates";
import ProgramTypes from "./components/ProgramTypes/ProgramTypes";
import { Progress } from "./components/Progress";
import ReflectionTemplates from "./components/ReflectionTemplates/ReflectionTemplates";
import Share from "./components/Share";
import TermManagement from "./components/TermManagement/TermManagement";
import { getErrorPageForErrorCode } from "./config/Config";
import { User } from "./Domain/user";
import ExternalRoutes from "./routes/externalRoutes";
import ProtectedRoutes from "./routes/protectedRoutes";
import { AcademicYearService } from "./services/AcademicYearService";
import { FeatureFlagService } from "./services/FeatureFlagService";
import { MasteryLevelService } from "./services/MasteryLevelService";
import { QualityCheckService } from "./services/QualityCheckService";
import { TermService } from "./services/TermService";
import { auth } from "./utils/auth";
import { isNotPublic } from "./utils/URIUtils";
import Course from "./views/Course/Course";
// Lazy-loaded so the Amplify UI CSS it imports (`@aws-amplify/ui-react/styles.css`)
// is code-split into its own chunk and only fetched/injected when a person
// actually navigates to `/login`. A static import pulls that CSS into the main
// bundle, where it applies globally and inflates nav/line-height on authenticated
// routes. CRA's webpack splits on this dynamic import() boundary.
const Login = lazy(() => import("./views/Login"));
import FinalizeActions from "./views/Dashboard/Faculty/FinalizeSectionResults/Actions/FinalizeActions";
import FinalizeHome from "./views/Dashboard/Faculty/FinalizeSectionResults/FinalizeHome";
import CanvasAssignment from "./views/Dashboard/Faculty/FinalizeSectionResults/FinalizeMethods/CanvasAssignment";
import LearningMasteryGradebook from "./views/Dashboard/Faculty/FinalizeSectionResults/FinalizeMethods/LearningMasteryGradebook";
import UploadScores from "./views/Dashboard/Faculty/FinalizeSectionResults/FinalizeMethods/UploadScores";
import FinalizeSectionResults from "./views/Dashboard/Faculty/FinalizeSectionResults/FinalizeSectionResults";
import FinalizeReflections from "./views/Dashboard/Faculty/FinalizeSectionResults/Reflections/FinalizeReflections";
import FinalizeSummary from "./views/Dashboard/Faculty/FinalizeSectionResults/Summary/FinalizeSummary";
import NotAuthorized from "./views/NotAuthorized";
import PageNotFound from "./views/PageNotFound";
import PublicCourseReview from "./views/Public/PublicCourseReview";
import PublicResults from "./views/Public/PublicResults";
import Signout from "./views/Signout";
import Status from "./views/Status";

import { EXTERNAL_MAIN_MIN_HEIGHT, MAIN_MIN_HEIGHT } from "./utils/constants";

const navWidth = 84;

/**
 * Element for the catch-all (`path="*"`) route INSIDE the `ProtectedRoutes`
 * guard. It only renders for an authenticated request (the guard redirects a
 * request with no Session_Cookie to `/login` before this element mounts), and
 * exists to avoid showing a misleading "Page not found" to a signed-in user
 * whose token has expired.
 *
 * The role-gated app routes (`/setup`, `/course`, etc.) are not registered until
 * `userDetails` has loaded with a role, so a deep link can land on this catch-all
 * in two very different situations:
 *
 *   - user data has NOT loaded yet (`userDetails.role` empty): either the initial
 *     bootstrap is still in flight, or the `ddl` cookie is stale and the App-level
 *     data-load has 401'd and is redirecting to `/login` (see ServiceHelper's 401
 *     handler). Either way the correct route may still appear, or a re-auth
 *     redirect is imminent — so render a neutral loading spinner, NOT "Page not
 *     found".
 *   - user data HAS loaded (`userDetails.role` set): the path is genuinely unknown
 *     for this user, so the not-found page is the right, non-misleading response.
 *
 * @param {import("./Domain/user").User} props.userDetails the current user
 */
const CatchAllRoute = ({ userDetails }) => {
  const { t } = useTranslation();
  if (!userDetails.role) {
    return (
      <Flex justifyItems="center" alignItems="center" height="50vh">
        <Flex.Item>
          <Spinner renderTitle={t("global.pleaseWait")} size="large" />
        </Flex.Item>
      </Flex>
    );
  }
  return <PageNotFound />;
};

const App = () => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const location = useLocation();
  // The Cognito auth pages (`/login`, `/signout`) must render the PUBLIC shell
  // (no authenticated `<Nav>` sidebar), like they do in incognito. A stale
  // `ddl` cookie can flip the cookie-presence `auth` heuristic to true on these
  // routes, which would otherwise render the authed Nav over the login/signout
  // content. Treat them as unauthenticated for the shell layout only.
  const isAuthPage = ["/login", "/signout"].includes(location.pathname);
  const authedShell = !!auth && !isAuthPage;
  const [userDetails, setUserDetails] = useState(new User());
  const [canvasTerms, setCanvasTerms] = useState([]);
  const [insightsTerms, setInsightsTerms] = useState([]);
  const [academicYears, setAcademicYears] = useState(null);
  const [masteryLevels, setMasteryLevels] = useState(null);
  const [ratingScales, setRatingScales] = useState(null);
  const [loadingTriggered, setLoadingTriggered] = useState(false);
  const [loadingDetails] = useState({
    loadingTriggered: loadingTriggered,
    setLoadingTriggered: setLoadingTriggered,
  });
  const [prevProg, setPrevProg] = useState(0);
  const [prog, setProg] = useState(0);
  const [totalOutcomes, setTotalOutcomes] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [progNotUpdatedCount, setProgNotUpdatedCount] = useState(0);
  const [wasErrorUpdating, setWasErrorUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(true);
  const [errorPage, setErrorPage] = useState(null);
  const [featureFlags, setFeatureFlags] = useState([]);

  const finalizeFlag =
    (Array.isArray(featureFlags) ? featureFlags : []).find(
      (f) => f.name === "finalize_results_flag"
    )?.value === "TRUE";

  useEffect(() => {
    const handleServiceError = (e) => setErrorPage(e.detail.errorPage);
    window.addEventListener("service-error", handleServiceError);
    return () =>
      window.removeEventListener("service-error", handleServiceError);
  }, []);

  useEffect(() => {
    // The Cognito auth pages (`/login`, `/signout`) must NOT run the
    // authenticated bootstrap. A stale/expired `ddl` cookie can still be present
    // in the browser there, making the cookie-presence `auth` heuristic true, so
    // without this guard `getUserById(-1)` / `getFeatureFlags` fire and fail:
    // on `/login` that produced a crash/blank screen, and on `/signout` (where
    // the session was just cleared) it pops a meaningless "failed to fetch
    // Institutional Configuration" error. These routes never need that data.
    const authPagePaths = ["/login", "/signout"];
    if (authPagePaths.includes(window.location.pathname)) {
      return;
    }
    // get current user (faculty: 184, pc: 447)
    if (auth) {
      UserService.getUserById(-1)
        .then((user) => {
          if (user) {
            if (!user.ddl_id) {
              setErrorPage(getErrorPageForErrorCode("User"));
            } else {
              setUserDetails(new User(user));
            }
          }
        })
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("notifications.userInfo"),
            }),
            ERROR
          )
        );
      if (isNotPublic(window.location.href)) {
        FeatureFlagService.getFeatureFlags({
          tenant: window.location.hostname.split(".")[0],
        })
          .then((flags) => {
            // A 401 (e.g. a stale cookie on /login) resolves the request to
            // `undefined` via ServiceHelper's redirect-suppressed 401 branch;
            // keep `featureFlags` an array so downstream `.find(...)` never
            // throws (which would blank the whole app — no error boundary).
            setFeatureFlags(Array.isArray(flags) ? flags : []);
          })
          .catch(() =>
            showNotification(
              t("notifications.general.fetchError", {
                obj: t("notifications.institutionalConfigInfo"),
              }),
              ERROR
            )
          );
      } else {
        console.debug("Public URL detected. Skipping feature flag retrieval.");
      }
    }
  }, [showNotification]);

  useEffect(() => {
    if (errorPage) {
      setErrorPage(null);
    }
  }, [errorPage]);

  useEffect(() => {
    // omit LO updates and its progress status for now
    // eslint-disable-next-line
    if (loadingTriggered && false) {
      if (progNotUpdatedCount > 60) {
        // prog not updated in over 60 iterations of this useEffect
        // to test this functionality, add break statement to backend per below but do not update the progress[1] value
        setLoadingTriggered(false);
        setUpdateSuccess(false);
        setWasErrorUpdating(true);
        setPrevProg(0);
        setProgNotUpdatedCount(0);
      } else {
        setPrevProg(prog);
        setLoadingTriggered(false);
        setUpdateSuccess(false);
        QualityCheckService.getQualityCheckProgress()
          .then(async (progress) => {
            setModalOpen(true);
            setProg(progress[0]);
            setTotalOutcomes(progress[1]);
            if (prevProg === prog) {
              setProgNotUpdatedCount(
                (progNotUpdatedCount) => progNotUpdatedCount + 1
              );
            } else {
              setProgNotUpdatedCount(0);
            }
            // to test with local backend w/o running all outcomes, add a break statement to events/outcome_update handler in
            // backend and set below progress to the total number of outcomes iterated over by that short-circuited loop
            if (progress[0] !== progress[1]) {
              await new Promise((resolve) => setTimeout(() => resolve(), 500));
              setLoadingTriggered(true);
            } else {
              // succeeded. reset vals and display success modal
              setUpdateSuccess(true);
              setPrevProg(0);
              setProgNotUpdatedCount(0);
              setProg(0);
              setTotalOutcomes(0);
            }
          })
          .catch((e) => {
            showNotification(
              `${t("notifications.app.qualityCheckError")} ${e}`,
              ERROR
            );
          });
      }
    }
  }, [
    loadingTriggered,
    prevProg,
    prog,
    progNotUpdatedCount,
    updateSuccess,
    showNotification,
  ]);

  useEffect(() => {
    if (auth && userDetails.role) {
      TermService.getTerms()
        .then((canvasTerms) => setCanvasTerms(canvasTerms))
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("notifications.obj.canvasTerms"),
            }),
            ERROR
          )
        );
      TermService.getInsightsTerms()
        .then((insightsTerms) => setInsightsTerms(insightsTerms))
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", { obj: t("terms") }),
            ERROR
          )
        );
      AcademicYearService.getAcademicYears()
        .then((academicYears) => setAcademicYears(academicYears))
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("academicYears.academicYears"),
            }),
            ERROR
          )
        );
      MasteryLevelService.getMasteryLevels()
        .then((masteryLevels) => setMasteryLevels(masteryLevels))
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("masteryLevels.masteryLevelsHeader"),
            }),
            ERROR
          )
        );
      MasteryLevelService.getRatingToMasteryLevelMaps()
        .then((ratingScales) => {
          if (
            !userDetails.isFaculty() &&
            ratingScales.some((map) => !map.is_mapped)
          ) {
            showNotification(
              <Text>
                {t("masteryLevels.unmappedAlert1")}{" "}
                <Link to="/setup/mastery">{t("masteryLevels.clickHere")}</Link>{" "}
                {t("masteryLevels.unmappedAlert2")}
              </Text>,
              WARNING
            );
          }
          setRatingScales(ratingScales);
        })
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("masteryLevels.assessmentScales"),
            }),
            ERROR
          )
        );
    }
  }, [userDetails.role, showNotification]);

  return (
    <MasteryLevelContext.Provider
      value={{ masteryLevels, setMasteryLevels, ratingScales, setRatingScales }}
    >
      <TermsContext.Provider
        value={{
          canvasTerms,
          setCanvasTerms,
          insightsTerms,
          setInsightsTerms,
          academicYears,
          setAcademicYears,
        }}
      >
        <FeatureContext.Provider value={featureFlags}>
          <UserDetailContext.Provider value={userDetails}>
            <LoadingContext.Provider value={loadingDetails}>
              <Flex
                justifyItems="space-between"
                alignItems="start"
                wrap={authedShell ? "no-wrap" : "wrap"}
                height="100%"
              >
                {isAllowedRoute(location.pathname) &&
                  (authedShell ? (
                    <Flex.Item size={`${navWidth}px`}>
                      <Nav width={navWidth} />
                    </Flex.Item>
                  ) : (
                    <Flex.Item size="100%">
                      <TopNav />
                    </Flex.Item>
                  ))}

                {errorPage && <Navigate to={errorPage} />}

                <Flex.Item shouldShrink shouldGrow height="100%">
                  <main
                    style={{
                      minHeight: authedShell
                        ? MAIN_MIN_HEIGHT
                        : EXTERNAL_MAIN_MIN_HEIGHT,
                      position: "relative",
                      padding: "1.5rem",
                    }}
                  >
                    <div
                      style={{
                        position: "fixed",
                        right: "25%",
                        left: "25%",
                        top: "10px",
                        zIndex: "9999",
                      }}
                    >
                      <SaveAlert />
                    </div>
                    <div
                      style={{
                        position: "fixed",
                        left: 100,
                        bottom: 0,
                        width: 500,
                        zIndex: "9999",
                      }}
                    >
                      <StatusAlert />
                    </div>

                    {!errorPage && (
                      <Routes>
                        <Route element={<ProtectedRoutes auth={!!auth} />}>
                          <Route path="/" exact element={<Dashboard />} />

                          <Route path="/courses" element={<Dashboard />} />
                          {(userDetails.isAC() ||
                            userDetails.isPC() ||
                            userDetails.isCC()) &&
                            finalizeFlag && (
                              <Route
                                path="/course-review"
                                element={<CourseReview />}
                              >
                                <Route
                                  path="home"
                                  element={<CourseReviewHome />}
                                />
                                <Route
                                  path="data-view"
                                  element={<CourseResults />}
                                />
                                <Route
                                  path="reflections"
                                  element={<CourseReflections />}
                                />
                                <Route
                                  path="actions"
                                  element={<CourseActions />}
                                />
                                <Route
                                  path="summary"
                                  element={<CourseReviewSummary />}
                                />
                              </Route>
                            )}
                          {finalizeFlag && (
                            <Route
                              path="/scores"
                              element={<FinalizeSectionResults />}
                            >
                              <Route path="home" element={<FinalizeHome />}>
                                <Route
                                  path=""
                                  element={
                                    <Alert>
                                      <Trans i18nKey="finalizeCourse.selectMethod" />
                                    </Alert>
                                  }
                                />
                                <Route
                                  path="assignment"
                                  element={<CanvasAssignment />}
                                />
                                <Route
                                  path="lmg"
                                  element={<LearningMasteryGradebook />}
                                />
                                <Route
                                  path="upload"
                                  element={<UploadScores />}
                                />
                              </Route>
                              <Route
                                path="reflections"
                                element={<FinalizeReflections />}
                              />
                              <Route
                                path="actions"
                                element={<FinalizeActions />}
                              />
                              <Route
                                path="summary"
                                element={<FinalizeSummary />}
                              />
                            </Route>
                          )}

                          {(userDetails.isAC() || userDetails.isPC()) && (
                            <>
                              <Route path="/course" element={<Course />} />
                              <Route path="/analyze" element={<Outcomes />} />
                              <Route
                                path="/mapping"
                                element={<CurriculumMap />}
                              />
                              <Route path="/setup" element={<Setup />}>
                                {userDetails.isAC() ? (
                                  <Route index element={<LearningTaxonomy />} />
                                ) : (
                                  <Route
                                    index
                                    element={<ProgramLearningOutcomes />}
                                  />
                                )}
                                {userDetails.isAC() && (
                                  <Route path="status" element={<Status />} />
                                )}
                                {userDetails.isAC() && (
                                  <Route
                                    path="taxonomy"
                                    element={<LearningTaxonomy />}
                                  />
                                )}
                                {userDetails.isAC() && (
                                  <Route
                                    path="ccp"
                                    element={<CourseCodePattern />}
                                  />
                                )}
                                {userDetails.isAC() && (
                                  <Route
                                    path="term-management"
                                    element={<TermManagement />}
                                  />
                                )}
                                <Route
                                  path="plos"
                                  element={<ProgramLearningOutcomes />}
                                />
                                <Route
                                  path="cycles"
                                  element={<CyclesAndGoals />}
                                />
                                <Route
                                  path="program-types"
                                  element={<ProgramTypes />}
                                />
                                <Route
                                  path="structure"
                                  element={<OrganizationalStructure />}
                                />
                                {userDetails.isAC() && (
                                  <>
                                    <Route
                                      path="ilos"
                                      element={<InstitutionalLearningOutcome />}
                                    />
                                    <Route
                                      path="attainment"
                                      element={<AttainmentLevelsConfig />}
                                    />
                                  </>
                                )}
                                <Route
                                  path="assignment"
                                  element={<AssignmentTypePanel />}
                                />
                                <Route
                                  path="mastery"
                                  element={<MasteryLevelsAndRatingScales />}
                                />
                                <Route
                                  path="plan-templates"
                                  element={<Templates />}
                                />
                                <Route
                                  path="reflection-templates"
                                  element={<ReflectionTemplates />}
                                />
                                <Route path="users" element={<Users />} />
                                {userDetails.isAC() && (
                                  <Route
                                    path="demographics"
                                    element={<Demographics />}
                                  />
                                )}
                              </Route>
                              <Route path="/plan" element={<Plan />} />
                              <Route
                                path="/plan/manage-plan"
                                element={<ManagePlan />}
                              />
                              <Route
                                path="/plan/create-plan-from-template"
                                element={<CreatePlanFromTemplate />}
                              />
                            </>
                          )}
                          {/*
                            Catch-all INSIDE the ProtectedRoutes guard. Many app
                            routes above (e.g. `/setup`, `/course`, `/analyze`,
                            `/mapping`, `/plan`) are only registered once
                            `userDetails` has loaded with a role (isAC/isPC/...).
                            Before that those `<Route>`s do not exist, so a deep
                            link like `/setup` would otherwise match NO route and
                            render a blank page ("No routes matched location").

                            Routing such an unmatched path through the guard makes
                            the no-session case correct: with no Session_Cookie the
                            guard redirects to `/login?originalDestination=<path>`
                            (so the person lands back on the requested page after
                            login) before this element ever renders.

                            The element itself (`CatchAllRoute`) then distinguishes
                            the two remaining cases so an authenticated user with an
                            expired token never sees a misleading "Page not found":
                            while user data is still loading (or a stale-cookie 401
                            re-auth redirect is in flight) it shows a spinner, and
                            only once the user has loaded with a role — meaning the
                            path is genuinely unknown for them — does it render the
                            not-found page.
                          */}
                          <Route
                            path="*"
                            element={
                              <CatchAllRoute userDetails={userDetails} />
                            }
                          />
                        </Route>
                        <Route element={<ExternalRoutes externalAuth={true} />}>
                          <Route path="/rater" element={<Rater />} />
                          <Route path="/results" element={<Results />} />
                          <Route
                            path="/results-report/:planId"
                            element={<ResultsReport />}
                          />
                          <Route
                            path="/assessment-status/:planId"
                            element={<AssessmentPlanningDashboard />}
                          />
                          <Route path="/share">
                            <Route
                              path=":tenantId/:shareId"
                              element={<Share />}
                            />
                          </Route>

                          <Route path="/error" element={<Error />} />

                          <Route path="" element={<Error />} />
                        </Route>
                        <Route path="/public">
                          <Route
                            path="public-results/:tenantId/:shareId"
                            exact
                            element={<PublicResults />}
                          />
                          <Route
                            path="public-course-review/:tenantId/:shareId"
                            exact
                            element={<PublicCourseReview />}
                          />
                          <Route path="" element={<PageNotFound />} />
                          <Route path="*" element={<PageNotFound />} />
                        </Route>
                        {/*
                          Public `/login` route (Amplify_Login_UI, IN-3003).
                          Declared OUTSIDE `<ProtectedRoutes>` so it is reachable
                          without a Session_Cookie — a person redirected here by
                          the auth guard has no `ddl` session yet. Renders the
                          Amplify `<Authenticator>` (Requirements 1.1, 2.1, 3.2).
                        */}
                        <Route
                          path="/login"
                          element={
                            <Suspense
                              fallback={
                                <Flex
                                  justifyItems="center"
                                  alignItems="center"
                                  height="50vh"
                                >
                                  <Flex.Item>
                                    <Spinner
                                      renderTitle={t("global.pleaseWait")}
                                      size="large"
                                    />
                                  </Flex.Item>
                                </Flex>
                              }
                            >
                              <Login />
                            </Suspense>
                          }
                        />
                        <Route path="/signout" element={<Signout />} />
                        <Route
                          path="/notauthorized"
                          element={<NotAuthorized />}
                        />
                      </Routes>
                    )}
                  </main>
                </Flex.Item>
              </Flex>

              <Progress
                success={updateSuccess}
                setParentProg={setProg}
                setTotalOutcomes={setTotalOutcomes}
                setModalOpen={setModalOpen}
                setShowErrorMessage={setWasErrorUpdating}
                setLoadingTriggered={setLoadingTriggered}
                loadingTriggered={loadingTriggered}
                showErrorMessage={wasErrorUpdating}
                currentProgress={prog}
                totalItems={totalOutcomes}
                item="outcomes"
                modalOpen={modalOpen}
                label={t("review.qualityCheck")}
                successText={t("progress.newQualityMetrics")}
                errorText={t("progress.qualitySettingsError")}
              />
            </LoadingContext.Provider>
          </UserDetailContext.Provider>
        </FeatureContext.Provider>
      </TermsContext.Provider>
    </MasteryLevelContext.Provider>
  );
};

export default App;
